import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Heart, Sparkles, Target } from 'lucide-react';

const VALUES = [
  { key: 'love', Icon: Heart },
  { key: 'luxury', Icon: Sparkles },
  { key: 'precision', Icon: Target }
];

const fadeX = (dir) => ({
  initial: { opacity: 0, x: dir },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
});

function About() {
  const { t } = useTranslation();

  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'About',
        page_path: window.location.pathname,
      });
    }
  }, []);

  const paragraphs = (key, className = 'mb-6') =>
    t(key, { returnObjects: true }).map((text, i, arr) => (
      <p key={i} className={`text-base md:text-lg leading-relaxed text-charcoal/80 ${i < arr.length - 1 ? className : ''}`}>
        {text}
      </p>
    ));

  return (
    <div className="about-page" data-testid="about-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-warm-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="overline mb-4">{t('about.overline')}</div>
          <h1 className="font-playfair text-5xl md:text-7xl tracking-tighter leading-[1.1] font-medium text-charcoal mb-6" data-testid="about-title">
            {t('about.title')}
          </h1>
        </div>
      </section>

      {/* Founders Story */}
      <section className="py-16 md:py-24 bg-warm-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {/* Jessica - Main Founder */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24">
            <motion.div {...fadeX(-20)}>
              <img 
                src="https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/3qwlep2e_IMG_8149.jpg"
                alt={t('about.alts.jessica')}
                className="rounded-3xl shadow-[0_16px_48px_rgba(59,48,45,0.1)] w-full h-[500px] object-cover"
                loading="lazy"
              />
            </motion.div>

            <motion.div {...fadeX(20)} data-testid="about-jessica">
              <div className="overline mb-4">{t('about.mainFounder')}</div>
              <h2 className="font-playfair text-4xl md:text-5xl tracking-tight leading-tight font-medium text-charcoal mb-6">
                Jessica Amber Rossi
              </h2>
              {paragraphs('about.jessica')}
            </motion.div>
          </div>

          {/* Jessica & Pasqualino Together */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24">
            <motion.div {...fadeX(-20)} className="order-2 lg:order-1" data-testid="about-pasqualino">
              <div className="overline mb-4">{t('about.coFounder')}</div>
              <h2 className="font-playfair text-4xl md:text-5xl tracking-tight leading-tight font-medium text-charcoal mb-6">
                Pasqualino Dachille
              </h2>
              {paragraphs('about.pasqualino')}
            </motion.div>

            <motion.div {...fadeX(20)} className="order-1 lg:order-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/adiyx4vi_IMG_8209.jpg"
                alt={t('about.alts.founders')}
                className="rounded-3xl shadow-[0_16px_48px_rgba(59,48,45,0.1)] w-full h-[500px] object-cover"
                loading="lazy"
              />
            </motion.div>
          </div>

          {/* Hunter & Brio */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24">
            <motion.div {...fadeX(-20)}>
              <img 
                src="https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/wbtqi53l_CasaPooch-45.jpg"
                alt={t('about.alts.dogs')}
                className="rounded-3xl shadow-[0_16px_48px_rgba(59,48,45,0.1)] w-full h-[500px] object-cover object-top"
                loading="lazy"
              />
            </motion.div>

            <motion.div {...fadeX(20)} data-testid="about-dogs">
              <div className="overline mb-4">{t('about.inspiration')}</div>
              <h2 className="font-playfair text-4xl md:text-5xl tracking-tight leading-tight font-medium text-charcoal mb-6">
                Hunter & Brio
              </h2>
              {paragraphs('about.dogs')}
            </motion.div>
          </div>

          {/* Community Connection */}
          <div className="bg-accent/10 rounded-3xl p-12 md:p-16" data-testid="about-community">
            <div className="max-w-3xl mx-auto text-center">
              <div className="overline mb-4">{t('about.community')}</div>
              <h2 className="font-playfair text-4xl md:text-5xl tracking-tight leading-tight font-medium text-charcoal mb-6">
                {t('about.rootedTitle')}
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-charcoal/80">
                {t('about.communityContent')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-warm-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {VALUES.map(({ key, Icon }, i) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="text-center"
                data-testid={`about-value-${key}`}
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon className="w-7 h-7 text-primary" strokeWidth={1.75} />
                </div>
                <h3 className="font-playfair text-2xl font-medium text-charcoal mb-4">{t(`about.values.${key}.title`)}</h3>
                <p className="text-charcoal/80 leading-relaxed">{t(`about.values.${key}.desc`)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;
